import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { MedicineBoxOutlined } from "@ant-design/icons";
import { Button, Divider, Table } from "antd";
import Title from "antd/lib/typography/Title";
import ReactQuill from "react-quill";
import _ from "lodash";

import notifier from "utils/notifier";
import { CarePlan, PacStatus, UserRole } from "utils/enums";
import { ErrorResponse } from "utils/types";
import { formatDate, handleDownload } from "utils/helpers";

import { SearchCriteria } from "store/eligibility/actions";

import DocumentCard from "components/Documents/Card";

import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";

interface CarePlansProps {
  getInfoForCarePlanPage(userId: string, pacType: string): any,
  getDocuments(userId: string): any,
  getUnreadTypeCount(userId: string, pacType: string): any,
  isFetchingInfoForCarePlan: boolean
  isFetchingCount: boolean,
  isFetching: boolean,

  //eligibility info page related
  search(searchCriteria: SearchCriteria): any,
  getProvider(providerKeyId: string, companyId: string): any,
  downloadMemberDocument(memberFilePath: string, memberFileName: string, membMpiNo: string): any,
  getPlans(id: string, companyId: string, userId: string): any,
  getNotes(userId: string, membMpiNo: string, companyId: string): any,
  updateReadInfo(passedUserId: string, passedPacUid: string, passedPacType: string): any,
}

const CarePlans = (props: CarePlansProps) => {
  const history = useHistory();
  const [carePlansDataSource, setCarePlansDataSource] = useState<Array<Object>>([]);
  const [careDocumentsDataSource, setCareDocumentsDataSource] = useState<Array<Object>>([]);
  const [unReadDocumentCount, setUnReadDocumentCount] = useState(0);
  const [unReadNoteCount, setUnReadNoteCount] = useState(0);
  const [unReadPlanCount, setUnReadPlanCount] = useState(0);
  const [showTable, setShowTable] = useState('Plans');

  const carePlansAndNoteColumns = [
    {
      title: "Description",
      key: "RmiDescription",
      dataIndex: "RmiDescription",
      render: (__: string, record: any) =>
      <ReactQuill
        className="mt-2"
        theme="bubble"
        readOnly={true}
        value={_.unescape(record.RmiDescription)}
      />
    },
    {
      title: "Date",
      key: "RmiCreatedDate",
      dataIndex: "RmiCreatedDate",
      render: (__: string, record: any) => formatDate(record.RmiCreatedDate, 'MM/DD/YYYY')
    },
    {
      title: "Member ID",
      key: "RmiMembId",
      dataIndex: "RmiMembId",
      render: (__: string, record: any) => <><Button type="link" onClick={() => goToCarePlanDetailPage(record, record.RmiDescription)}>{record.RmiMembId}</Button></>
    },
    {
      title: "Created By",
      key: "RmiCreatedBy",
      dataIndex: "RmiCreatedBy",
    },
  ];

  const careDocumentColumns = [
    {
      title: "File Name",
      key: "RmiOrigFileName",
      dataIndex: "RmiOrigFileName",
      render: (__: string, record: any) => <a onClick={() => downloadMemberDocument(record)}>{record.RmiOrigFileName}</a>
    },
    {
      title: "Member ID",
      key: "RmiMembId",
      dataIndex: "RmiMembId",
    },
    {
      title: "Member Name",
      key: "RmiRevFullName",
      dataIndex: "RmiRevFullName",
    },
    {
      title: "Upload Date",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (__: string, record: any) => formatDate(record.RmiUploadDate, 'MM/DD/YYYY [at] h:mmA')
    },
    {
      title: "Uploaded By",
      key: "RmiUploadBy",
      dataIndex: "RmiUploadBy",
    },
  ];

  useEffect(() => {
    getUnreadTypeCount();
    onShowTable(CarePlan.PLAN); //Care plan table loads by default on page load
  },[]);

  //Using the same search eligibility table search API
  const search = (carePlanMemberInfo: any, description: string) => {
    //According to legacy code
    const provider = (sessionStorage.user_role === UserRole.CASE || sessionStorage.user_role === UserRole.IPA) ? "OTHER" : "PCP";
    let formData = {
      "DateOfBirth": carePlanMemberInfo.RmiBirth,
      "MemberId": carePlanMemberInfo.RmiMembId,
      "Provider": provider,
      "UserId": sessionStorage.user_id
    }

    return props.search(formData)
      .then((response: any) => {
        if(response.length > 0) {
          getProvider(response[0], description);
        } else {
          notifier.error('No access. Please use eligibility to search for this member.');
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getProvider = (memberInfo: any, description: string) => {
    return props.getProvider(memberInfo.RmiProvKeyId, memberInfo.RmiCompanyId)
      .then((primaryCareProvider: Object) => {

        //calling extra API for the RmiPlanId or RmiNoteId
        if(showTable === CarePlan.PLAN) getPlans(memberInfo, description, primaryCareProvider);
        if(showTable === CarePlan.NOTE) getNotes(memberInfo, description, primaryCareProvider)
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  //Legacy search eligibility detail page needs memberInfo, primary care providers info, and getPlan info from each separate API for the "Care Plan Detail Page"
  //1. calls the search eligibility table API
  //2. uses the memberInfo from the API to call the primary provider API
  //3. uses the memberInfo to call getPlan API to get the PlanId that is not provided by getInfoForCarePlanPage() API
  //4. uses the memberInfo, primary care provider, and the getPlan to get to the specific "Care Plan Detail Page"
  const goToCarePlanDetailPage = (carePlanMemberInfo: any, description: string) => {
    search(carePlanMemberInfo, description);
  }

  //Extra API to get the RmiPlandId. Using RmiDescription to filter that specific object data in getPlans that is used for Care Plan detail page.
  //TODO remove this API once getInfoForCarePlanPage() have planId included
  const getPlans = (memberInfo: any, description: string, primaryCareProvider: Object) => {
    return props.getPlans(sessionStorage.user_id, memberInfo.RmiMembMpiNo, memberInfo.RmiCompanyId)
      .then((response: any) => {
        const plan = response.find((plan: any) => plan.RmiDescription === description);
        updateReadInfo(showTable, plan.RmiPlanId);
        history.push({
          pathname: '/eligibility/pacp',
          state: {
            eligibilityMemberInfo: memberInfo,
            primaryCareProvider: primaryCareProvider,
            planId: plan.RmiPlanId,
            status: PacStatus[plan.RmiStatusId],
            pacPlan: plan,
            breadcrumbs:  [
              { pathname: '/quality/overview', name: 'Home' },
              { pathname: '/eligibility/search', name: 'Member Search' },
              {
                pathname: '/eligibility/member-detail', name: 'Member Eligibility and Assessment',
                state: { eligibilityMemberInfo: memberInfo }
              },
              { pathname: '/eligibility/pacp', name: 'Post Acute Care Plan' }
            ]
          }
        });
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const updateReadInfo = (pacType: string, pacUid: string) => {
    return props.updateReadInfo(sessionStorage.user_id, pacUid, pacType )
    .then()
    .catch((err: ErrorResponse) => {
      notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });
  }

  //TODO: Slow API. Should accept some pagination on the API call like the Search Fax page.
  //TODO: This API is missing RmiPlanId! Work around is to call an extra API (PacPlan?Id=) for now to get the RmiPlanId that is needed to go to Pac Plan detail page.
  //TODO: This API is missing RmiNoteId! Work around is to call an extra API (PacNote?Id=) for now to get the RmiNoteId that is needed to go to Pac Note detail page.
  //Care plan and care note Table
  const getInfoForCarePlanPage = (pacType: string) => {
    return props.getInfoForCarePlanPage(sessionStorage.user_id, pacType)
      .then((response: any) => {
        setCarePlansDataSource(response);
      })
      .catch((err: ErrorResponse) => {
        //TODO: api taking too long causing 'response' not defined
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  /**
   * To store the member eligibility data to go back later,
   * we store the props data inside the history state for the breadcrumb
   * just like the route format.
   */
  const getNotes = (memberInfo: any, description: string, primaryCareProvider: Object) => {
    return props.getNotes(sessionStorage.user_id, memberInfo.RmiMembMpiNo, memberInfo.RmiCompanyId)
      .then((response: any) => {
        const note = response.find((note: any) => note.RmiDescription === description);
        updateReadInfo(showTable, note.RmiNoteId);
        history.push({
          pathname: '/eligibility/pacn',
          state: {
            eligibilityMemberInfo: memberInfo,
            primaryCareProvider: primaryCareProvider,
            noteId: note.RmiNoteId,
            pacNote: note,
            breadcrumbs:  [
              { pathname: '/quality/overview', name: 'Home' },
              { pathname: '/eligibility/search', name: 'Member Search' },
              {
                pathname: '/eligibility/member-detail', name: 'Member Eligibility and Assessment',
                state: { eligibilityMemberInfo: memberInfo }
              },
              { pathname: '/eligibility/pacn', name: 'Post Acute Care Note' }
            ]
          }
        });
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  //Care documents
  const getDocuments = () => {
    return props.getDocuments(sessionStorage.user_id)
      .then((response: any) => {
        setCareDocumentsDataSource(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  //Download Documents API when you click on the links on the table
  //document too long to define
  const downloadMemberDocument = (document: any) => {
    return props.downloadMemberDocument(document.RmiFilePath, document.RmiFileName, document.RmiUid) //TODO Need to test this. Legacy code is using RmiUid but it should be RmiMembMpiNo
      .then((response: any) => {
        handleDownload(response, document.RmiFileName);
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  const onShowTable = (showTable: string) =>{

    if(showTable === CarePlan.PLAN) {
      setShowTable(showTable);
      getInfoForCarePlanPage(CarePlan.PLAN);
    }

    if(showTable === CarePlan.NOTE) {
      setShowTable(showTable);
      getInfoForCarePlanPage(CarePlan.NOTE);
    }

    if(showTable === CarePlan.DOC) {
      setShowTable(showTable);
      getDocuments();
    }
  }

  const getUnreadTypeCount = async () => {
    //unread count for documents
    await props.getUnreadTypeCount(sessionStorage.user_id, CarePlan.DOC)
      .then((response: number) => setUnReadDocumentCount(response))
      .catch((err: ErrorResponse) =>
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.')
      );

    //unread count for notes
    await props.getUnreadTypeCount(sessionStorage.user_id, CarePlan.NOTE)
      .then((response: number) => setUnReadNoteCount(response))
      .catch((err: ErrorResponse) =>
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.')
      );


    //unread count for post acute care plans
    await props.getUnreadTypeCount(sessionStorage.user_id, CarePlan.PLAN)
      .then((response: number) => setUnReadPlanCount(response))
      .catch((err: ErrorResponse) =>
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  return (
    <>
      <div className="row flex-lg-wrap justify-content-center mb-2">
        <DocumentCard
          title={CarePlan.POST_ACUTE_CARE_PLANS}
          docCount={unReadPlanCount ? unReadPlanCount : 0}
          docType={CarePlan.PLANS}
          icon={<MedicineBoxOutlined />}
          loading={props.isFetchingCount}
          onClick={() => onShowTable(CarePlan.PLAN)}
        />
        <DocumentCard
          title={CarePlan.POST_ACUTE_CARE_NOTES}
          docCount={unReadNoteCount ? unReadNoteCount : 0}
          docType={CarePlan.NOTES}
          icon={<MedicineBoxOutlined />}
          loading={props.isFetchingCount}
          onClick={() => onShowTable(CarePlan.NOTE)}
        />
        <DocumentCard
          title={CarePlan.CARE_PLANS_DOCUMENTS}
          docCount={unReadDocumentCount ? unReadDocumentCount : 0}
          docType={CarePlan.DOCUMENTS}
          icon={<MedicineBoxOutlined />}
          loading={props.isFetchingCount}
          onClick={() => onShowTable(CarePlan.DOC)}
        />
      </div>

      <Divider />

      { showTable === CarePlan.PLAN &&
        <div className="flex-lg-wrap mt-2 mb-2">
          <Title level={5}>{CarePlan.POST_ACUTE_CARE_PLANS}</Title>
          <Table
            columns={carePlansAndNoteColumns}
            dataSource={carePlansDataSource}
            pagination={{ position: ["bottomCenter"] }}
            loading={props.isFetchingInfoForCarePlan}
          />
        </div>
      }

      <Divider />

      { showTable === CarePlan.NOTE  &&
        <div className="flex-lg-wrap mt-2 mb-2">
          <Title level={5}>{CarePlan.POST_ACUTE_CARE_NOTES}</Title>
          <Table
            columns={carePlansAndNoteColumns}
            dataSource={carePlansDataSource}
            pagination={{ position: ["bottomCenter"] }}
            loading={props.isFetchingInfoForCarePlan}
          />
        </div>
      }

      <Divider />

      { showTable === CarePlan.DOC &&
        <div className="flex-lg-wrap mt-2 mb-2">
          <Title level={5}>{CarePlan.CARE_PLANS_DOCUMENTS}</Title>
          <Table
            columns={careDocumentColumns}
            dataSource={careDocumentsDataSource}
            pagination={{ position: ["bottomCenter"] }}
            loading={props.isFetching}
            rowKey={'RmiOrigFileName'}
          />
        </div>
      }
    </>
  );
};

export default CarePlans;